<template>
  <p class="text-body-small">
    {{ t("created") }}
    {{ formatDate(data.created_at, { format: DateTime.DATETIME_MED }) }}
    <span v-if="!isDFM" class="inline">
      • Via
      <Button v-if="data.meta?.returnportal_name" :variant="ButtonVariant.Ghost" class="inline">
        {{ t("return_portal.name") }} {{ data.meta?.returnportal_name }}
      </Button>
      <template v-else>{{ data.order_context || "API" }}</template>
    </span>
  </p>
  <header class="flex items-center gap-2.5">
    <Avatar
      :size="ComponentSize.lg"
      :entity="
        data
          ? {
              name: data.carrier,
              image: getCarrierLogoUrl(data.carrier),
            }
          : {}
      "
    />
    <div class="flex flex-col">
      <p class="text-body-large-m">
        {{ data?.package_number || "-" }}
      </p>
      <p class="text-body-default">
        {{ data?.reference || "-" }}
      </p>
    </div>
  </header>
  <TrackingIndicator
    :title="trackingStatus?.title"
    :color="trackingColor(trackingStatus?.event_code)"
    :icon="trackingIcon(trackingStatus?.event_code)"
  />
  <div
    class="text-body-default [&>*:nth-child(even)]: [&>*:nth-child(odd)]:odd:text-fgr-primary grid w-full grid-cols-[max-content_1fr] gap-x-2 gap-y-3.5 [&>*:nth-child(odd)]:odd:break-words"
  >
    <p>{{ t("carrier") }}</p>
    <p>{{ data?.carrier_name || "-" }}</p>
    <p>{{ t("product") }}</p>
    <p>{{ data?.title || "-" }}</p>
    <p>{{ t("dimensions") }}</p>
    <p>
      {{
        `${t("heightShort")}${data?.height || "-"}cm x ${t("widthShort")}${data?.width || "-"}cm x
      ${t("lengthShort")}${data?.length || "-"}cm`
      }}
    </p>
    <p>{{ t("weight") }}</p>
    <p>{{ useFormatWeight(data?.weight || 0) }}</p>
    <p>{{ t("price") }}</p>
    <p>{{ priceWithCurrency }}</p>
    <p>{{ t("labelless") }}</p>
    <p>
      {{ data?.labelless_code ? t("yes") : t("no") }}
    </p>
    <p>{{ t("pallet_number") }}</p>
    <p>
      {{ data?.meta?.pallet_number?.join(", ") || "-" }}
    </p>
    <p>{{ t("insurance") }}</p>
    <p>{{ insurance }}</p>
    <p>{{ t("ordered_via") }}</p>
    <p>{{ data?.order_context || "API" }}</p>
    <p class="self-center">{{ t("document", 99) }}</p>
    <Badge icon="FileText">
      {{ data?.documents_count || 0 }}
      {{ t("document", data?.documents_count || 99) }}
    </Badge>
  </div>
</template>

<script lang="ts" setup>
  import { DateTime } from "luxon";
  import type { IShipmentSimple } from "~/types/shipment";

  const { data } = defineProps<{
    data: IShipmentSimple;
  }>();
  const { t } = useI18n();
  const { formatDate } = useDate();
  const { isDFM } = storeToRefs(useWorkspacesStore());
  const { hasService } = useServicesStore();

  const priceWithCurrency = computed(() => {
    if (!data?.price) return "-";
    if (data.price.type === "consignment") return t("consignment_grouping_price");
    return useFormatPrice(useDecidePriceExclOrIncl(data.price), true, data.price.currency);
  });

  // Each insurance field carries its own currency - format from that, not from data.price.
  const insurance = computed(() => {
    if (!data) return "-";

    return formatShipmentInsurance(data, t);
  });

  const trackingStatus = computed(() => {
    return data?.package_journey_status || data?.latest_tracking_event;
  });

  const { trackingColor, trackingIcon } = useTrackingColors();
</script>
